'use client';

import { useQuery } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';

export interface OnboardingLetterSignOff {
  /**
   * The block that prints below the handwritten signature: the reader's own
   * letter footer, or the firm's default name/title block. Empty when the firm
   * prints nothing there.
   */
  html: string;
  signatureFileId: number | null;
  /**
   * `default` is NOT an empty state — it is what the reader's letters print
   * today. `none` means the firm has turned the name off, which 23 of 74
   * production firms have.
   */
  source: 'personal' | 'default' | 'none';
}

/**
 * What the acting user's letters actually print below their handwritten
 * signature, resolved server-side.
 *
 * **The setup flow's signature preview binds to this rather than composing a
 * sign-off out of the reader's name and title** (VALE-1830). Composing it here
 * got three things wrong that only the backend can know: a reader with a
 * personal letter footer was shown a name/title block their letters never
 * print; the name and title were shown unconditionally, where the real block is
 * gated on the firm's `includeSignerNameAfterSignature` and
 * `includeSignerTitleAfterSignature`; and nothing in the frontend had those
 * settings to consult. The endpoint calls `resolveEffectiveLetterFooter`, which
 * is what letter generation itself calls, so the preview cannot drift from the
 * document — the same guarantee VALE-687 established for the per-correspondence
 * preview in the draft editor.
 */
export function useOnboardingLetterSignOff() {
  return useQuery<OnboardingLetterSignOff>({
    queryKey: ['onboarding', 'letter-sign-off'],
    queryFn: () =>
      directApiClient.get<OnboardingLetterSignOff>(
        '/onboarding/letter-sign-off'
      ),
    // The signature and the identity rows are both edited on this very screen,
    // so a stale answer would show the reader the sign-off they had a moment
    // ago. The `['onboarding']` prefix is invalidated by both write paths.
    staleTime: 0
  });
}
